/**
 * FP-CARD
 *
 * Reusable product card widget (collection grids, related-products blocks, PDP
 * recommendations). Reads its variant data from two embedded JSON <script>
 * tags and keeps price/image/availability in sync with whichever variant the
 * shopper's swatch selections currently resolve to.
 *
 * A Web Component (rather than a page-wide init pass) because this card is
 * injected into the DOM in more than one way: server-rendered in a collection
 * loop, but also fetched and swapped in asynchronously by Shopify's native
 * <product-recommendations> element on the product page. connectedCallback
 * fires either way, so no MutationObserver or "already initialized" guard is
 * needed to catch the async case.
 */

class FpCard extends HTMLElement {
  connectedCallback() {
    this.picker = this.querySelector('.fp-variants');
    this.priceEl = this.querySelector('[data-fp="price"]');
    this.compareEl = this.querySelector('[data-fp="compare"]');
    this.badgeEl = this.querySelector('[data-fp="badge"]');
    this.atcBtn = this.querySelector('[data-fp="atc"]');

    const { variants, priceMap } = this.readEmbeddedData();
    this.variants = variants;
    this.priceMap = priceMap;

    if (this.picker) {
      this.picker.addEventListener('change', this.handleUpdate);
      this.picker.addEventListener('input', this.handleUpdate);
      this.picker.addEventListener('click', this.handlePickerClick);
    }

    if (this.atcBtn) {
      this.atcBtn.addEventListener('click', this.handleAddToCart, { capture: true });
    }

    this.update();
  }

  disconnectedCallback() {
    if (this.picker) {
      this.picker.removeEventListener('change', this.handleUpdate);
      this.picker.removeEventListener('input', this.handleUpdate);
      this.picker.removeEventListener('click', this.handlePickerClick);
    }

    if (this.atcBtn) {
      this.atcBtn.removeEventListener('click', this.handleAddToCart, { capture: true });
    }
  }

  readEmbeddedData = () => {
    try {
      const variantsEl = this.querySelector('.fp-product-variants');
      const priceMapEl = this.querySelector('.fp-variant-price-map');
      const variants = JSON.parse(variantsEl ? variantsEl.textContent : '[]') || [];
      const priceMap = JSON.parse(priceMapEl ? priceMapEl.textContent : '{}') || {};
      return { variants, priceMap };
    } catch {
      return { variants: [], priceMap: {} };
    }
  };

  normalize = (value) => String(value ?? '').trim().toLowerCase();

  selectedValues = () => {
    const values = [];
    for (const fieldset of (this.picker || this).querySelectorAll('.fp-option')) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) values.push(String(checked.value));
    }
    return values;
  };

  findVariant = (selection) => {
    const byOptions = this.variants.find((variant) => {
      if (!variant) return false;

      const options = [];
      if ('option1' in variant) options.push(String(variant.option1));
      if ('option2' in variant && variant.option2 != null) options.push(String(variant.option2));
      if ('option3' in variant && variant.option3 != null) options.push(String(variant.option3));

      if (options.length !== selection.length) return false;
      return options.every((option, index) => this.normalize(option) === this.normalize(selection[index]));
    });
    if (byOptions) return byOptions;

    const key = this.normalize(selection.join(' / '));
    return this.variants.find((variant) => this.normalize(variant?.title) === key) || null;
  };

  currentVariant = () => {
    const selection = this.selectedValues();
    const matched = selection.length ? this.findVariant(selection) : null;
    return matched || this.variants.find((variant) => variant?.available) || this.variants[0] || null;
  };

  optionFieldsets = () =>
    [...(this.picker || this).querySelectorAll('.fp-option')].sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );

  fieldsetSelection = (fieldset) => {
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    return checked ? String(checked.value) : null;
  };

  /**
   * Hide/disable option-N swatches that have no variant matching the
   * currently selected values of the options before it (e.g. Color swatches
   * that don't belong to the selected Range).
   *
   * If nothing downstream is valid for the prior selection, that selection
   * can't actually be reached from any real variant — leave this fieldset
   * unfiltered instead of hiding every swatch, which would otherwise strand
   * it with nothing checked and force a silent fallback to an unrelated
   * variant in currentVariant().
   */
  refreshCascade = () => {
    const fieldsets = this.optionFieldsets();

    for (let i = 1; i < fieldsets.length; i += 1) {
      const fieldset = fieldsets[i];
      const priorSelections = fieldsets.slice(0, i).map(this.fieldsetSelection);
      if (priorSelections.some((value) => value == null)) continue;

      const validValues = new Set();
      for (const variant of this.variants) {
        if (!variant) continue;

        const matchesPrior = priorSelections.every(
          (value, index) => this.normalize(variant[`option${index + 1}`]) === this.normalize(value)
        );
        if (!matchesPrior) continue;

        const value = variant[`option${i + 1}`];
        if (value != null) validValues.add(this.normalize(value));
      }

      if (validValues.size === 0) continue;

      let hasValidChecked = false;
      for (const label of fieldset.querySelectorAll('label.fp-swatch')) {
        const input = label.querySelector('input[type="radio"]');
        if (!input) continue;

        const isValid = validValues.has(this.normalize(input.value));
        label.hidden = !isValid;
        input.disabled = !isValid;
        if (isValid && input.checked) hasValidChecked = true;
      }

      if (!hasValidChecked) {
        const firstValid = fieldset.querySelector('label.fp-swatch:not([hidden]) input[type="radio"]');
        if (firstValid) firstValid.checked = true;
      }
    }
  };

  applyVariant = (variant) => {
    const priceInfo = this.priceMap[String(variant.id)];
    if (!priceInfo) return;

    if (this.priceEl) this.priceEl.textContent = priceInfo.price_formatted || '';

    const hasDiscount = priceInfo.compare_at_price && priceInfo.compare_at_price > priceInfo.price;

    if (this.compareEl) {
      this.compareEl.textContent = priceInfo.compare_formatted || '';
      this.compareEl.hidden = !hasDiscount;
    }

    if (this.badgeEl) {
      if (hasDiscount) {
        const pct = Math.round(((priceInfo.compare_at_price - priceInfo.price) * 100) / priceInfo.compare_at_price);
        this.badgeEl.textContent = `-${pct}%`;
        this.badgeEl.hidden = pct <= 0;
      } else {
        this.badgeEl.hidden = true;
      }
    }

    if (priceInfo.url) {
      for (const link of this.querySelectorAll('a[href]')) link.setAttribute('href', priceInfo.url);
    }

    if (this.atcBtn) {
      this.atcBtn.dataset.variantId = String(variant.id);
      this.atcBtn.disabled = !priceInfo.available;
      this.atcBtn.title = priceInfo.available ? 'Add to cart' : 'Sold out';
    }
  };

  update = () => {
    this.refreshCascade();
    const variant = this.currentVariant();
    if (variant) this.applyVariant(variant);
  };

  handleUpdate = () => this.update();

  handlePickerClick = (event) => {
    const { target } = event;
    if (target && (target.tagName === 'INPUT' || target.closest('label'))) this.update();
  };

  handleAddToCart = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    const variant = this.currentVariant();
    if (!variant?.id) return;

    const { atcBtn } = this;

    try {
      atcBtn.classList.add('fp-atc--loading');
      atcBtn.disabled = true;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity: 1 }),
      });
      if (!response.ok) throw new Error('Add to cart failed');
      await response.json();

      atcBtn.classList.remove('fp-atc--loading');
      atcBtn.classList.add('fp-atc--added');

      // Silent: do NOT open the cart drawer.
      document.dispatchEvent(new Event('fp:cart:silent-add'));
      setTimeout(() => {
        atcBtn.classList.remove('fp-atc--added');
        atcBtn.disabled = false;
      }, 1200);
    } catch {
      atcBtn.classList.remove('fp-atc--loading');
      atcBtn.disabled = false;
    }
  };
}

if (!customElements.get('fp-card')) {
  customElements.define('fp-card', FpCard);
}
